import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ServiceConfigApis } from "@core/ability";

// 服务类型枚举
export enum ServiceType {
  PUBLIC = "public",
  PRIVATE = "private",
}

// 配置数据接口
export interface ServiceConfigData {
  serviceType: ServiceType;
  serviceAddress: string;
}

interface ServiceConfigStore {
  // 配置数据
  config: ServiceConfigData;
  // 加载状态
  loading: boolean;
  // 是否已初始化
  initialized: boolean;
  // 操作方法
  setConfig: (config: ServiceConfigData) => void;
  initializeConfig: () => Promise<void>;
  saveConfig: (config: ServiceConfigData) => Promise<boolean>;
  resetConfig: () => void;
}

// 默认配置
const defaultConfig: ServiceConfigData = {
  serviceType: ServiceType.PUBLIC,
  serviceAddress: "",
};

const useServiceConfigStore = create<ServiceConfigStore>()(
  persist(
    (set, get) => ({
      // 初始状态
      config: defaultConfig,
      loading: false,
      initialized: false,
      
      // 设置配置
      setConfig: (config: ServiceConfigData) => {
        set({
          config,
        });
      },
      
      // 初始化配置 - 从服务器获取
      initializeConfig: async () => {
        const state = get();
        if (state.initialized || state.loading) {
          return; // 避免重复初始化
        }
        
        set({ loading: true });

        try {
          const serverConfig = await ServiceConfigApis.getServiceConfig();
          if (serverConfig) {
            set({
              config: {
                serviceType: serverConfig.serviceType as ServiceType,
                serviceAddress: serverConfig.serviceAddress || '',
              },
              loading: false,
              initialized: true,
            });
          } else {
            // 服务器没有配置，使用默认配置
            set({
              config: defaultConfig,
              loading: false,
              initialized: true,
            });
          }
        } catch (error) {
          console.error("初始化服务配置失败:", error);
          set({
            config: defaultConfig,
            loading: false,
            initialized: true,
          });
        }
      },
      
      // 保存配置到服务器
      saveConfig: async (config: ServiceConfigData) => {
        try {
          const success = await ServiceConfigApis.setServiceConfig(config);
          if (success) {
            set({ config });
            return true;
          }
          return false;
        } catch (error) {
          console.error("保存服务配置失败:", error);
          return false;
        }
      },
      
      // 重置配置
      resetConfig: () => {
        set({
          config: defaultConfig,
          initialized: false,
        });
      },
    }),
    {
      name: "service-config-storage",
      version: 1,
      // 只持久化配置数据，不持久化loading和initialized状态
      partialize: (state) => ({ config: state.config }),
    }
  )
);

export default useServiceConfigStore;
